import { useMemo, useState, type ReactNode } from 'react'
import toString from 'lodash/toString'
import { Check, ChevronsUpDown } from 'lucide-react'
import { INCOTERM_OPTIONS } from '@klear/constants/options'
import { ORDER_EVENT_OPERATIONAL_TITLES } from '@klear/order-event/constants'
import type { OperationalFieldChange, OperationalForm, OperationalFormErrorMap } from '@klear/operational/wire.client'
import { MoneyInput } from '@klear/ui/components/money-input'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Combobox } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'
import { Button } from '@klear/ui/components/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@klear/ui/components/command'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'
import { useSuppliers } from '@klear/ui/hooks/use-suppliers'
import { cn } from '@klear/ui/lib/utils'

interface OperationalFieldsProps {
  traderId: number
  values: OperationalForm
  errors: OperationalFormErrorMap
  onFieldChange: OperationalFieldChange
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-destructive">{message}</p> : null
}

function TitleCombobox({
  value,
  error,
  onChange
}: {
  value: string
  error?: string
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const options = useMemo(
    (): { value: string; label: string; keywords: string[] }[] =>
      ORDER_EVENT_OPERATIONAL_TITLES.map((title) => ({
        value: title,
        label: title,
        keywords: [title]
      })),
    []
  )

  const displayOptions = useMemo(() => {
    const result = [...options]
    const trimmed = value.trim()
    if (trimmed && !options.some((o) => o.value.toLowerCase() === trimmed.toLowerCase())) {
      result.unshift({ value: trimmed, label: trimmed, keywords: [trimmed] })
    }
    return result
  }, [options, value])

  const filteredOptions = useMemo(() => {
    if (!searchValue) return displayOptions
    const searchLower = searchValue.toLowerCase()
    return displayOptions.filter(
      (option) =>
        option.keywords?.some((keyword) => keyword.toLowerCase().includes(searchLower)) ||
        option.label.toLowerCase().includes(searchLower)
    )
  }, [displayOptions, searchValue])

  const selectedLabel = displayOptions.find((option) => option.value === value)?.label

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Title</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full justify-between', error && 'border-destructive')}
          >
            {selectedLabel ?? 'Select title…'}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput
              placeholder="Search or type custom title…"
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchValue.trim()) {
                  e.preventDefault()
                  onChange(searchValue.trim())
                  setOpen(false)
                  setSearchValue('')
                }
              }}
            />
            <CommandList>
              <CommandEmpty>
                {searchValue.trim() ? (
                  <div className="py-2 text-sm text-muted-foreground">
                    Press Enter to add &quot;{searchValue.trim()}&quot;
                  </div>
                ) : (
                  'No results.'
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    className="cursor-pointer"
                    value={option.value}
                    keywords={option.keywords}
                    onSelect={() => {
                      onChange(option.value === value ? '' : option.value)
                      setOpen(false)
                      setSearchValue('')
                    }}
                  >
                    {option.label}
                    <Check className={cn('ml-auto', value === option.value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError message={error} />
    </div>
  )
}

export function OperationalDetailsFields({
  traderId,
  values,
  errors,
  onFieldChange,
  createSupplierAction
}: OperationalFieldsProps & {
  createSupplierAction?: ReactNode
}) {
  const { suppliers } = useSuppliers(traderId)
  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: toString(s.id),
        label: s.name
      })),
    [suppliers]
  )
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TitleCombobox value={values.title} error={errors.title} onChange={(title) => onFieldChange('title', title)} />
        <div className="space-y-2">
          <Label className="text-sm font-medium">Reference Number</Label>
          <Input value={values.number} onChange={(e) => onFieldChange('number', e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Supplier</Label>
          <Combobox
            value={values.supplierId}
            options={supplierOptions}
            placeholder="Search suppliers…"
            triggerLabel="Select supplier…"
            onChange={(v) => onFieldChange('supplierId', v)}
            error={errors.supplierId ? { message: errors.supplierId } : undefined}
          />
          {createSupplierAction}
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Incoterm</Label>
          <Combobox
            value={values.incoterm}
            options={[...INCOTERM_OPTIONS]}
            placeholder="Search incoterms…"
            triggerLabel="Select incoterm…"
            onChange={(v) => onFieldChange('incoterm', v)}
          />
        </div>
      </div>
    </div>
  )
}

export function OperationalTermsFields({ values, errors, onFieldChange }: OperationalFieldsProps) {
  return (
    <div className="space-y-4">
      <MoneyInput value={values.amount} onChange={(m) => onFieldChange('amount', m)} errorMessage={errors.amount} />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Start Date</Label>
          <DatePicker value={values.effectiveDate} onChange={(date) => date && onFieldChange('effectiveDate', date)} />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Delivery Date</Label>
          <DatePicker value={values.endDate} onChange={(date) => onFieldChange('endDate', date)} />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Notes</Label>
        <Textarea value={values.notes} onChange={(e) => onFieldChange('notes', e.target.value)} />
      </div>
    </div>
  )
}
